import { useEffect, useState } from "react";
import { apiUrl } from "../utils/api";

/**
 * Berufsstatus-Optionen — EINE Quelle für alle Masken.
 *
 * Vorher lag dieselbe 5er-Liste („Angestellt", „Selbstständig", „Beamte",
 * „Azubi", „Sonstige") an vier Stellen hartkodiert, während der gepflegte
 * Katalog `ContactBerufsstatus` (Backend, `GET /api/contact-berufsstatus`)
 * gar nicht abgerufen wurde. Die beiden Vokabulare waren bis auf „Sonstige"
 * disjunkt — ein Wert aus dem Katalog erschien im Dropdown als leer.
 *
 * Der Katalog ist global (kein `tenantId`), ändert sich praktisch nie und ist
 * klein, deshalb: einmal laden, modulweit cachen, bei Fehlschlag auf die
 * eingebaute Liste zurückfallen. So ist die Auswahl auch dann bedienbar, wenn
 * der Endpoint klemmt.
 */

/**
 * Fallback-Katalog, deckungsgleich mit `contactBerufsstatus.seed.service.ts`
 * und der Migration 202607260004. Nur relevant, wenn der Endpoint nicht
 * antwortet — im Normalfall gewinnen die Werte aus der Datenbank.
 */
export const BERUFSSTATUS_FALLBACK = [
  "Angestellte/r",
  "Angestellte/r öffentlicher Dienst",
  "Arbeiter/in",
  "Auszubildende/r",
  "Beamter/Beamtin",
  "Beamter/Beamtin; Richter/in auf Lebenszeit",
  "Freiberufler/in",
  "Geschäftsführer/in",
  "Gesellschafter-Geschäftsführer/in",
  "Hausfrau/-mann",
  "Kind",
  "Kindererziehende/r",
  "Landwirt/in",
  "Minijobber/in",
  "Pensionär/in",
  "Rentner/in",
  "Schüler/in",
  "Selbstständige/r",
  "Soldat/in",
  "Sonstige",
  "Student/in",
  "z.Z. ohne Berufstätigkeit",
] as const;

type BerufsstatusRow = { id: number; berufsstatus?: string | null };

let cache: string[] | null = null;
let inFlight: Promise<string[]> | null = null;

async function loadBerufsstatus(): Promise<string[]> {
  if (cache) return cache;
  if (inFlight) return inFlight;

  // NUR der Erfolgsfall landet im Cache. Der Fallback wird bewusst nicht
  // gecacht: sonst nagelt ein einziger Fehlschlag — etwa der Backend-Neustart
  // beim DEV-Deploy — die gesamte Browser-Session auf die eingebaute Liste
  // fest, bis jemand hart neu lädt. Sobald Katalog und Konstante auseinander
  // laufen (der Katalog ist per API pflegbar), bearbeiteten genau diese Nutzer
  // still gegen eine veraltete Liste.
  const p = (async () => {
    try {
      const res = await fetch(apiUrl("contact-berufsstatus"));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rows: BerufsstatusRow[] = await res.json();
      const werte = rows
        .map((r) => (r.berufsstatus ?? "").trim())
        .filter((v) => v.length > 0);
      // Ein leerer Katalog (Seed lief nie) darf die Auswahl nicht lahmlegen —
      // gilt aber auch nicht als geladen, damit der nächste Mount neu versucht.
      if (!werte.length) return [...BERUFSSTATUS_FALLBACK];
      cache = werte;
      return cache;
    } catch (e) {
      console.error("Berufsstatus-Katalog laden fehlgeschlagen", e);
      return [...BERUFSSTATUS_FALLBACK];
    } finally {
      inFlight = null;
    }
  })();

  inFlight = p;
  return p;
}

/**
 * Ergänzt einen vorhandenen Ist-Wert, der nicht im Katalog steht, als
 * zusätzliche Option. Ohne das rendert ein natives `<select>` den Datensatz
 * als leer — der Wert steht in der Datenbank, ist aber unsichtbar, und die
 * erste Auswahl eines Anwenders ersetzt ihn stillschweigend. Dasselbe Muster
 * nutzt die Detailmaske bereits beim Familienstand.
 */
export function withBerufsstatusValue(options: string[], value?: string | null): string[] {
  const v = (value ?? "").trim();
  if (!v || options.includes(v)) return options;
  return [...options, v];
}

/**
 * Lädt den Berufsstatus-Katalog (modulweit gecacht) und liefert ihn als
 * String-Liste. `withValue` ergänzt bei Bedarf den aktuellen Wert des
 * Datensatzes.
 */
export function useBerufsstatusOptions(currentValue?: string | null) {
  const [options, setOptions] = useState<string[]>(() => cache ?? [...BERUFSSTATUS_FALLBACK]);

  useEffect(() => {
    let mounted = true;
    loadBerufsstatus().then((werte) => {
      if (mounted) setOptions(werte);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return withBerufsstatusValue(options, currentValue);
}
